import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Settings, 
  Plus,
  Check,
  X,
  Clock,
  AlertTriangle,
  Info,
  CheckCircle,
  User,
  Users,
  Smartphone
} from 'lucide-react';

interface Notification {
  id: string;
  type: 'review_assigned' | 'review_overdue' | 'review_completed' | 'escalation' | 'system';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  read: boolean;
  created_at: string;
  data?: any;
}

interface NotificationChannel {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'in_app' | 'slack' | 'webhook';
  enabled: boolean;
  config: any;
}

export function ReviewNotificationCenter() {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [newChannelType, setNewChannelType] = useState('email');
  const [newChannelConfig, setNewChannelConfig] = useState('');

  // Mock notification data
  useEffect(() => {
    const mockNotifications: Notification[] = [
      {
        id: '1',
        type: 'review_assigned',
        title: 'New Review Assignment',
        message: 'You have been assigned to review Bill #INV-2024-001 from ABC Utilities',
        priority: 'high',
        read: false,
        created_at: new Date(Date.now() - 10 * 60 * 1000).toISOString()
      },
      {
        id: '2',
        type: 'review_overdue',
        title: 'Review Overdue',
        message: 'Bill #INV-2024-002 review is overdue by 2 hours',
        priority: 'urgent',
        read: false,
        created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
      },
      {
        id: '3',
        type: 'review_completed',
        title: 'Review Completed',
        message: 'Bill #INV-2024-003 has been successfully reviewed and approved',
        priority: 'low',
        read: true,
        created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
      },
      {
        id: '4',
        type: 'escalation',
        title: 'Review Escalated',
        message: 'Bill #INV-2024-004 has been escalated to supervisor due to validation issues',
        priority: 'high',
        read: false,
        created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
      },
      {
        id: '5',
        type: 'system',
        title: 'Workflow Updated',
        message: 'Auto-assignment workflow has been updated with new rules',
        priority: 'medium',
        read: true,
        created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      }
    ];

    const mockChannels: NotificationChannel[] = [
      {
        id: '1',
        name: 'Email Notifications',
        type: 'email',
        enabled: true,
        config: { email: user?.email }
      },
      {
        id: '2',
        name: 'In-App Notifications',
        type: 'in_app',
        enabled: true,
        config: {}
      },
      {
        id: '3',
        name: 'SMS Alerts',
        type: 'sms',
        enabled: false,
        config: { phone: '+1234567890' }
      }
    ];

    setNotifications(mockNotifications);
    setChannels(mockChannels);
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const toggleChannel = (id: string) => {
    setChannels(prev => prev.map(c => 
      c.id === id ? { ...c, enabled: !c.enabled } : c
    ));
  };

  const addNotificationChannel = () => {
    const newChannel: NotificationChannel = {
      id: Date.now().toString(),
      name: `${newChannelType.charAt(0).toUpperCase() + newChannelType.slice(1)} Channel`,
      type: newChannelType as any,
      enabled: true,
      config: JSON.parse(newChannelConfig || '{}')
    };

    setChannels(prev => [...prev, newChannel]);
    setNewChannelType('email');
    setNewChannelConfig('');
    setShowNewChannel(false);
    
    toast({
      title: "Notification channel added",
      description: "Your new notification channel has been configured.",
    });
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'high': return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'medium': return <Info className="h-4 w-4 text-primary" />;
      case 'low': return <CheckCircle className="h-4 w-4 text-success" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'destructive';
      case 'high': return 'warning';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'review_assigned': return <User className="h-4 w-4" />;
      case 'review_overdue': return <Clock className="h-4 w-4" />;
      case 'review_completed': return <CheckCircle className="h-4 w-4" />;
      case 'escalation': return <AlertTriangle className="h-4 w-4" />;
      case 'system': return <Settings className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'sms': return <Smartphone className="h-4 w-4" />;
      case 'in_app': return <Bell className="h-4 w-4" />;
      case 'slack': return <MessageSquare className="h-4 w-4" />;
      case 'webhook': return <Settings className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Notification Center</h2>
            <p className="text-muted-foreground">Manage your review notifications and alerts</p>
          </div>
          {unreadCount > 0 && (
            <Badge variant="destructive" className="ml-2">
              {unreadCount} unread
            </Badge>
          )}
        </div>
        <div className="flex space-x-2">
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead}>
              <Check className="mr-2 h-4 w-4" />
              Mark All Read
            </Button>
          )}
          <Dialog open={showSettings} onOpenChange={setShowSettings}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Notification Settings</DialogTitle>
                <DialogDescription>
                  Configure your notification preferences and channels.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-4">
                  <h4 className="font-medium">Notification Channels</h4>
                  {channels.map((channel) => (
                    <div key={channel.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getChannelIcon(channel.type)}
                        <div>
                          <div className="font-medium">{channel.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {channel.type.charAt(0).toUpperCase() + channel.type.slice(1)} notifications
                          </div>
                        </div>
                      </div>
                      <Switch
                        checked={channel.enabled}
                        onCheckedChange={() => toggleChannel(channel.id)}
                      />
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Notification Types</h4>
                    <Dialog open={showNewChannel} onOpenChange={setShowNewChannel}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Channel
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Notification Channel</DialogTitle>
                          <DialogDescription>
                            Configure a new notification channel.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="channelType">Channel Type</Label>
                            <Select value={newChannelType} onValueChange={setNewChannelType}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="sms">SMS</SelectItem>
                                <SelectItem value="slack">Slack</SelectItem>
                                <SelectItem value="webhook">Webhook</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="channelConfig">Configuration (JSON)</Label>
                            <Textarea
                              id="channelConfig"
                              value={newChannelConfig}
                              onChange={(e) => setNewChannelConfig(e.target.value)}
                              placeholder='{"email": "user@example.com"}'
                              rows={3}
                            />
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button variant="outline" onClick={() => setShowNewChannel(false)}>
                            Cancel
                          </Button>
                          <Button onClick={addNotificationChannel}>
                            Add Channel
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="space-y-3">
                    {['Review Assignments', 'Overdue Alerts', 'Escalations', 'System Updates'].map((type) => (
                      <div key={type} className="flex items-center justify-between">
                        <Label>{type}</Label>
                        <Switch defaultChecked />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Notifications</TabsTrigger>
          <TabsTrigger value="unread">Unread ({unreadCount})</TabsTrigger>
          <TabsTrigger value="reviews">Reviews</TabsTrigger>
          <TabsTrigger value="escalations">Escalations</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Notifications</CardTitle>
              <CardDescription>
                Complete history of your review notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <div className="space-y-4">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`flex items-start space-x-3 p-4 border rounded-lg transition-colors ${
                        !notification.read ? 'bg-muted/50' : ''
                      }`}
                    >
                      <div className="flex-shrink-0 mt-1">
                        {getTypeIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-foreground">
                            {notification.title}
                          </p>
                          <div className="flex items-center space-x-2">
                            <Badge variant={getPriorityColor(notification.priority) as any}>
                              {notification.priority}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatTimeAgo(notification.created_at)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center space-x-2 mt-2">
                          {!notification.read && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => markAsRead(notification.id)}
                            >
                              <Check className="mr-1 h-3 w-3" />
                              Mark as Read
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => deleteNotification(notification.id)}
                          >
                            <X className="mr-1 h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="unread" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Unread Notifications</CardTitle>
              <CardDescription>
                Notifications that require your attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notifications.filter(n => !n.read).map((notification) => (
                  <div
                    key={notification.id}
                    className="flex items-start space-x-3 p-4 border rounded-lg bg-muted/50"
                  >
                    <div className="flex-shrink-0 mt-1">
                      {getPriorityIcon(notification.priority)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">
                          {notification.title}
                        </p>
                        <Badge variant={getPriorityColor(notification.priority) as any}>
                          {notification.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {notification.message}
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => markAsRead(notification.id)}
                        >
                          <Check className="mr-1 h-3 w-3" />
                          Mark as Read
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {notifications.filter(n => !n.read).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No unread notifications
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Review Notifications</CardTitle>
              <CardDescription>
                Notifications related to review tasks and assignments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notifications.filter(n => 
                  n.type.includes('review') || n.type === 'escalation'
                ).map((notification) => (
                  <div
                    key={notification.id}
                    className={`flex items-start space-x-3 p-4 border rounded-lg ${
                      !notification.read ? 'bg-muted/50' : ''
                    }`}
                  >
                    <div className="flex-shrink-0 mt-1">
                      {getTypeIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">
                          {notification.title}
                        </p>
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {notification.message}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="escalations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Escalation Alerts</CardTitle>
              <CardDescription>
                High-priority escalations requiring immediate attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notifications.filter(n => 
                  n.type === 'escalation' || n.priority === 'urgent'
                ).map((notification) => (
                  <div
                    key={notification.id}
                    className="flex items-start space-x-3 p-4 border-2 border-destructive/20 rounded-lg bg-destructive/5"
                  >
                    <div className="flex-shrink-0 mt-1">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">
                          {notification.title}
                        </p>
                        <Badge variant="destructive">
                          {notification.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {notification.message}
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <Button size="sm" variant="destructive">
                          Take Action
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => markAsRead(notification.id)}
                        >
                          Acknowledge
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {notifications.filter(n => 
                  n.type === 'escalation' || n.priority === 'urgent'
                ).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No escalations at this time
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}